import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { styled } from '@compiled/react';
import { LoadingButton } from '@atlaskit/button';
import { token } from '@atlaskit/tokens';
import { type IntlShape, injectIntl } from '@atlassian/jira-intl/src/index.tsx';
import { DATE_PICKER_TYPES } from './connected-date-pickers-with-validator/constants.tsx';
import DatePickersWithValidator from './connected-date-pickers-with-validator/main.tsx';
import messages from './messages.tsx';
import TimeBreakdownDropdown from './time-breakdown/main.tsx';
import type { DateTimeBreakdown } from './time-breakdown/types.tsx';
import {
	getAvailableOptionsByDateRange,
	getTimeBreakdownByDateRange,
	getAllowedDateRange,
} from './utils.tsx';

type Props = {
	loading: boolean;
	dateTimeBreakdown: DateTimeBreakdown;
	allowedDateRange?: {
		min: Date;
		max: Date;
	};
	onUpdate: (arg1: DateTimeBreakdown) => void;
	intl: IntlShape;
};

const DatePickerGroup = ({
	intl,
	loading,
	dateTimeBreakdown,
	onUpdate,
	allowedDateRange = getAllowedDateRange(),
}: Props) => {
	const [selectedStart, setStart] = useState({
		date: dateTimeBreakdown.startDate,
		invalidMessages: [],
	});
	const [selectedEnd, setEnd] = useState({
		date: dateTimeBreakdown.endDate,
		invalidMessages: [],
	});
	const [timeBreakdown, setTimeBreakdown] = useState(dateTimeBreakdown.timeBreakdown);

	const updateBtnDisabled = useMemo((): boolean => {
		const isDatePickerInvalid =
			selectedStart.invalidMessages.length > 0 || selectedEnd.invalidMessages.length > 0;

		if (isDatePickerInvalid) {
			return isDatePickerInvalid;
		}

		return (
			selectedStart.date === dateTimeBreakdown.startDate &&
			selectedEnd.date === dateTimeBreakdown.endDate &&
			timeBreakdown === dateTimeBreakdown.timeBreakdown
		);
	}, [
		dateTimeBreakdown.endDate,
		dateTimeBreakdown.startDate,
		dateTimeBreakdown.timeBreakdown,
		selectedEnd.date,
		selectedEnd.invalidMessages.length,
		selectedStart.date,
		selectedStart.invalidMessages.length,
		timeBreakdown,
	]);

	const availableTimeBreakdownOptions = useMemo(
		() =>
			getAvailableOptionsByDateRange({
				startDate: selectedStart.date,
				endDate: selectedEnd.date,
			}),
		[selectedEnd.date, selectedStart.date],
	);

	const selectedTimeBreakdownId = useMemo(
		() => getTimeBreakdownByDateRange(availableTimeBreakdownOptions, { timeBreakdown }),
		[availableTimeBreakdownOptions, timeBreakdown],
	);

	const onUpdateButtonClick = useCallback(() => {
		onUpdate({
			startDate: selectedStart.date,
			endDate: selectedEnd.date,
			timeBreakdown: selectedTimeBreakdownId,
		});
	}, [onUpdate, selectedEnd.date, selectedStart.date, selectedTimeBreakdownId]);

	const onSelectedDateRangeUpdate = ({
		// @ts-expect-error - TS7031 - Binding element 'type' implicitly has an 'any' type.
		type,
		// @ts-expect-error - TS7031 - Binding element 'date' implicitly has an 'any' type.
		date,
		// @ts-expect-error - TS7031 - Binding element 'startDateErrorMessages' implicitly has an 'any' type. | TS7031 - Binding element 'endDateErrorMessages' implicitly has an 'any' type.
		invalidMessages: { startDateErrorMessages, endDateErrorMessages },
	}) => {
		const isStartDate = type === DATE_PICKER_TYPES.START;
		setStart((prev) => ({
			date: isStartDate ? date : prev.date,
			invalidMessages: startDateErrorMessages,
		}));
		setEnd((prev) => ({
			date: isStartDate ? prev.date : date,
			invalidMessages: endDateErrorMessages,
		}));
	};

	useEffect(() => {
		setStart({ date: dateTimeBreakdown.startDate, invalidMessages: [] });
		setEnd({ date: dateTimeBreakdown.endDate, invalidMessages: [] });
		setTimeBreakdown(dateTimeBreakdown.timeBreakdown);
	}, [dateTimeBreakdown]);

	return (
		<GroupedDatePickerWrapper data-test-id="servicedesk-reports-common.ui.date-filter.date-picker-group.grouped-date-picker-wrapper">
			<Spaced>
				<DatePickersWithValidator
					loading={loading}
					allowedDateRange={allowedDateRange}
					selectedStart={selectedStart}
					selectedEnd={selectedEnd}
					onUpdate={onSelectedDateRangeUpdate}
				/>
			</Spaced>
			<Spaced>
				<TimeBreakdownDropdown
					loading={loading}
					selectedTimeBreakdownId={selectedTimeBreakdownId}
					availableTimeBreakdownOptions={availableTimeBreakdownOptions}
					onTimeBreakdownSelect={(breakdown) => setTimeBreakdown(breakdown)}
				/>
			</Spaced>
			<UpdateButtonWrapper>
				<LoadingButton
					isDisabled={!loading && updateBtnDisabled}
					isLoading={loading}
					onClick={onUpdateButtonClick}
					appearance="primary"
				>
					{intl.formatMessage(messages.update)}
				</LoadingButton>
			</UpdateButtonWrapper>
		</GroupedDatePickerWrapper>
	);
};

export default injectIntl(DatePickerGroup);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupedDatePickerWrapper = styled.div({
	display: 'flex',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Spaced = styled.div({
	marginLeft: token('space.300', '24px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UpdateButtonWrapper = styled.div({
	margin: `${token('space.500', '40px')} 0 0 ${token('space.300', '24px')}`,
});
